import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { timeConverter } from '../../utils'; // Assuming timeConverter is already defined
import Swal from 'sweetalert2';

const Countdown = ({ countdownTime, timeOver, setTimeTaken, isPaused }) => {
  const totalTime = countdownTime * 1000; // Convert to milliseconds
  const [timerTime, setTimerTime] = useState(totalTime);

  useEffect(() => {
    // Reset timer when countdownTime changes
    setTimerTime(totalTime);
  }, [countdownTime, totalTime]);

  useEffect(() => {
    if (isPaused) return undefined;

    const timer = setInterval(() => {
      setTimerTime(prevTime => {
        if (prevTime <= 1000) { // If less than or equal to 1 second
          clearInterval(timer);
          timeOver(totalTime); // Pass total time when time is up

          Swal.fire({
            icon: 'info',
            title: `Oops! Time's up.`,
            text: 'See how you did!',
            confirmButtonText: 'Check Results',
            timer: 5000,
          });

          return 0; // Stop the timer
        }

        return prevTime - 1000; // Decrease by 1 second
      });
    }, 1000);

    return () => clearInterval(timer); // Cleanup on unmount
  }, [totalTime, timeOver, isPaused]); // Dependencies for useEffect

  // Update the time taken every second
  useEffect(() => {
    setTimeTaken(totalTime - timerTime); // Update the time taken
  }, [timerTime, totalTime, setTimeTaken]);

  // Convert timerTime into hours, minutes, and seconds
  const { hours, minutes, seconds } = timeConverter(timerTime);
  
  // Calculate percentage of time remaining
  const timePercentage = (timerTime / totalTime) * 100;
  const isLowTime = timePercentage < 20; // Less than 20% time remaining

  return (
    <div className="bg-white rounded-xl shadow-2xl p-3 sm:p-4 border-2 border-gray-200">
      <div className="flex flex-col items-center space-y-2">
        <div className={`text-xs sm:text-sm font-semibold ${isLowTime ? 'text-red-600' : 'text-gray-600'}`}>
          Time Left
        </div>
        <div className="flex items-center gap-1.5 sm:gap-2">
          {/* Hour */}
          {hours > 0 && (
            <div className="flex flex-col items-center">
              <div className={`bg-gradient-to-br from-blue-600 to-purple-700 text-white py-1.5 sm:py-2 px-2 sm:px-3 rounded-lg shadow-md text-lg sm:text-xl md:text-2xl font-bold min-w-[40px] sm:min-w-[50px] text-center ${isLowTime ? 'animate-pulse' : ''}`}>
                {hours.toString().padStart(2, '0')}
              </div>
              <div className="text-[8px] sm:text-xs text-gray-500 mt-0.5">H</div>
            </div>
          )}
          
          {hours > 0 && (
            <div className="text-lg sm:text-xl md:text-2xl font-bold text-gray-400">:</div>
          )}
          
          {/* Minute */}
          <div className="flex flex-col items-center">
            <div className={`bg-gradient-to-br from-blue-600 to-purple-700 text-white py-1.5 sm:py-2 px-2 sm:px-3 rounded-lg shadow-md text-lg sm:text-xl md:text-2xl font-bold min-w-[40px] sm:min-w-[50px] text-center ${isLowTime ? 'animate-pulse' : ''}`}>
              {minutes.toString().padStart(2, '0')}
            </div>
            <div className="text-[8px] sm:text-xs text-gray-500 mt-0.5">M</div>
          </div>
          
          <div className="text-lg sm:text-xl md:text-2xl font-bold text-gray-400">:</div>
          
          {/* Second */}
          <div className="flex flex-col items-center">
            <div className={`bg-gradient-to-br from-blue-600 to-purple-700 text-white py-1.5 sm:py-2 px-2 sm:px-3 rounded-lg shadow-md text-lg sm:text-xl md:text-2xl font-bold min-w-[40px] sm:min-w-[50px] text-center ${isLowTime ? 'animate-pulse' : ''}`}>
              {seconds.toString().padStart(2, '0')}
            </div>
            <div className="text-[8px] sm:text-xs text-gray-500 mt-0.5">S</div>
          </div>
        </div>
      </div>
    </div>
  );
};

Countdown.propTypes = {
  countdownTime: PropTypes.number.isRequired,
  timeOver: PropTypes.func.isRequired,
  setTimeTaken: PropTypes.func.isRequired,
  isPaused: PropTypes.bool,
};

Countdown.defaultProps = {
  isPaused: false,
};

export default Countdown;
